import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  IconButton,
  Box,
  Tooltip,
  Stack,
  Chip,
  useTheme,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import BlockIcon from "@mui/icons-material/Block";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { getAllOffers } from "../../../../services/pos/Tables";
import { getAllProducts } from "../../../../services/menuPage";
import EditOfferModal from "./EditOfferModal";
import LinkOfferModal from "./LinkOfferModal";
import UnlinkOfferModal from "./UnlinkOfferModal";
import DeactivateOfferDialog from "./DeactivateOfferDialog";
import ViewOfferModal from "./ViewOfferModal";


const OfferList = ({ refresh = 0 }) => {
  const theme = useTheme();
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]); // new state to hold products
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tagFilter, setTagFilter] = useState(""); // new: filter by name_tag / name_tage
  const [categoryFilter, setCategoryFilter] = useState(""); // new: filter by product category
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [linkOfferId, setLinkOfferId] = useState(null);

  // helper to show date as YYYY-MM-DD (works if created_at is already "YYYY-MM-DD..." or full ISO)
  const formatDateISO = (val) => {
    if (!val) return "—";
    if (typeof val === "string" && val.length >= 10) return val.slice(0, 10);
    try {
      return new Date(val).toISOString().slice(0, 10);
    } catch {
      return String(val);
    }
  };

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // derive unique tags from offers
  const uniqueTags = useMemo(() => {
    const s = new Set();
    offers.forEach((o) => {
      const t = (o.name_tag || o.name_tage || "").toString();
      if (t) s.add(t);
    });
    return Array.from(s);
  }, [offers]);

  // derive unique product categories from loaded products
  const uniqueCategories = useMemo(() => {
    const s = new Set();
    (Array.isArray(products) ? products : Array.isArray(products?.data) ? products.data : []).forEach((p) => {
      if (p?.category_name) s.add(p.category_name);
    });
    return Array.from(s);
  }, [products]);

  // compute filtered offers by name, description, created_at, or product names
  const filteredOffers = useMemo(() => {
    if (!debouncedSearch && !tagFilter && !categoryFilter) return offers;
    const q = debouncedSearch.toLowerCase();
    return offers.filter((offer) => {
      // if tagFilter set, require match
      if (tagFilter) {
        const tagValue = (offer.name_tag || offer.name_tage || "").toString();
        if (tagValue !== tagFilter) return false;
      }

      // if categoryFilter set, require that the offer has at least one item whose product.category_name matches
      if (categoryFilter) {
        const items = offer.items || offer.itmes || [];
        const hasCat = items.some((it) => {
          const prod = products.find((p) => p.id == it.product_id);
          return prod && prod.category_name === categoryFilter;
        });
        if (!hasCat) return false;
      }
      // check offer fields
      if ((offer.name || "").toLowerCase().includes(q)) return true;
      if ((offer.description || "").toLowerCase().includes(q)) return true;
      if ((formatDateISO(offer.created_at) || "").toLowerCase().includes(q)) return true;

      // check product names in items
      const items = offer.items || offer.itmes || [];
      for (const it of items) {
        const prod = products.find((p) => p.id == it.product_id);
        if (prod && (prod.name || "").toLowerCase().includes(q)) return true;
        // also check by product_id numeric match
        if (String(it.product_id).includes(q)) return true;
      }
      // if we only filtered by tag (no search) we should include the offer (tag already matched above)
      return q.length === 0 && !!tagFilter;
    });
  }, [debouncedSearch, offers, products, tagFilter, categoryFilter]);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const [offersRes, productsRes] = await Promise.all([getAllOffers(), getAllProducts()]);

      const offerList = Array.isArray(offersRes)
        ? offersRes
        : Array.isArray(offersRes.data)
        ? offersRes.data
        : [];

      const productList = Array.isArray(productsRes)
        ? productsRes
        : Array.isArray(productsRes.data)
        ? productsRes.data
        : [];

      setOffers(offerList);
      setProducts(productList);
    } catch (err) {
      alert("❌ " + err.message);
      setOffers([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, [refresh]);

  const handleEditClick = (offer) => {
    setSelectedOffer(offer);
    setEditModalOpen(true);
  };

  const handleLinkClick = (offerId) => {
    setLinkOfferId(offerId);
    setLinkModalOpen(true);
  };

  const handleUnlinkClick = (offerId) => {
    setLinkOfferId(offerId);
    setUnlinkModalOpen(true);
  };

  const handleDeactivateClick = (offer) => {
    setSelectedOffer(offer);
    setDeactivateDialogOpen(true);
  };

  const handleViewClick = (offer) => {
    setSelectedOffer(offer);
    setViewModalOpen(true);
  };

  if (loading) return <CircularProgress />;

  return (
    <>
      {/* Search bar */}
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="offer-tag-filter-label">فلتر التصنيف</InputLabel>
          <Select
            labelId="offer-tag-filter-label"
            label="فلتر التاج"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
          >
            <MenuItem value="">الكل</MenuItem>
            {uniqueTags.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="offer-category-filter-label">فلتر المجموعة</InputLabel>
          <Select
            labelId="offer-category-filter-label"
            label="فلتر المجموعة"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">كل المجموعات</MenuItem>
            {uniqueCategories.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="ابحث عن عروض أو منتجات أو تواريخ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          النتائج: {filteredOffers.length}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {filteredOffers.map((offer, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 4,
                position: "relative",
                backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#fdfdfd",
                color: theme.palette.text.primary,
              }}
            >
              <Stack spacing={1} alignItems="center" textAlign="center">
                <Box display="flex" justifyContent="center" gap={1} mb={1}>
                  <Tooltip title="تعديل العرض">
                    <IconButton onClick={() => handleEditClick(offer)} color="inherit">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="عرض التفاصيل">
                    <IconButton onClick={() => handleViewClick(offer)} color="inherit">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="ربط بالفرع">
                    <IconButton onClick={() => handleLinkClick(offer.id)} color="inherit">
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="إزالة من الفرع">
                    <IconButton onClick={() => handleUnlinkClick(offer.id)} color="inherit">
                      <LinkOffIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="إيقاف العرض">
                    <IconButton onClick={() => handleDeactivateClick(offer)} color="inherit">
                      <BlockIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography variant="h6" fontWeight="bold">{offer.name}</Typography>
                  <Chip label={offer.name_tag || offer.name_tage} color="primary" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" fontStyle="italic">{offer.description}</Typography>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">📅 <strong>الفترة:</strong></Typography>
                  <Typography variant="body2" color="text.secondary">{new Date(offer.start_date).toLocaleDateString()} → {new Date(offer.end_date).toLocaleDateString()}</Typography>
                </Stack>

                <Typography variant="body2">🕒 <strong>تاريخ الإنشاء:</strong> {formatDateISO(offer.created_at)}</Typography>

                {/* حالة العرض */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2"><strong>الحالة:</strong></Typography>
                  <Chip
                    label={offer.status || "—"}
                    size="small"
                    color={offer.status === "active" ? "success" : "default"}
                    sx={{ textTransform: "capitalize" }}
                  />
                </Box>
  
                <Typography variant="body2">🏷️ <strong>الخصم:</strong> {offer.discount} جنيه</Typography>
                <Typography variant="body2">📦 <strong>عدد المنتجات:</strong> {offer.items_number}</Typography>
                <Typography variant="body2">💰 <strong>السعر الإجمالي:</strong> {offer.total_price} جنيه</Typography>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">🛒 المنتجات:</Typography>
                <Stack spacing={0.5} pl={2}>
                  {(() => {
                    const allItems = offer.items || offer.itmes || [];
                    const displayed = categoryFilter
                      ? allItems.filter((it) => {
                          const prod = products.find((p) => p.id == it.product_id);
                          return prod && prod.category_name === categoryFilter;
                        })
                      : allItems;

                    if (displayed.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary">— لا توجد منتجات للمجموعة المختارة</Typography>
                      );
                    }

                    return displayed.map((item, idx) => {
                      const prod = products.find((p) => p.id == item.product_id);
                      const prodName = prod ? `${prod.category_name ? prod.category_name + " - " : ""}${prod.name}` : `منتج #${item.product_id}`;
                      return (
                        <Typography key={idx} variant="body2">
                          ▸ {prodName} | 💵 {item.price} جنيه | 🔢 {item.quantity} قطعة | 📝 {item.note || "—"}
                        </Typography>
                      );
                    });
                  })()}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {editModalOpen && selectedOffer && (
        <EditOfferModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          initialData={selectedOffer}
          onSuccess={loadOffers}
        />
      )}

      {viewModalOpen && selectedOffer && (
        <ViewOfferModal
          isOpen={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          offerId={selectedOffer.id}
        />
      )}

      {linkModalOpen && (
        <LinkOfferModal
          isOpen={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          offerId={linkOfferId}
          onSuccess={loadOffers}
        />
      )}

      {unlinkModalOpen && (
        <UnlinkOfferModal
          isOpen={unlinkModalOpen}
          onClose={() => setUnlinkModalOpen(false)}
          offerId={linkOfferId}
          onSuccess={loadOffers}
        />
      )}

      {deactivateDialogOpen && selectedOffer && (
        <DeactivateOfferDialog
          open={deactivateDialogOpen}
          onClose={() => setDeactivateDialogOpen(false)}
          offer={selectedOffer}
          onSuccess={loadOffers}
        />
      )}
    </>
  );
};

export default OfferList;